import { useAppSelector } from "integrations/redux/hooks"
import { LineWidth } from "@nova/constants"
import { Line } from "@nova/protobuf"
import { convertHexStringToRGBArray } from "@nova/util-strings"
import {
    selectAllFirePerimeterBufferLines,
    selectFirePerimetersMap,
} from "features/entity-caching/fire-perimeters/selectors"
import GeometryLayer from "plugins/shared/layers/GeometryLayer.class"
import { useMemo } from "react"
import { selectEditingFirePerimeterId, selectSelectedFirePerimeterId } from "../FirePerimeterSlice"
import { Geometry } from "plugins/shared/layers/types"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { defaultFirePerimeterColorDeckGL } from "../constants"

export const usePerimeterBuffersLayer = () => {
    const selectedFirePerimeterId = useAppSelector(selectSelectedFirePerimeterId)
    const polygonBeingEditedId = useAppSelector(selectEditingFirePerimeterId())
    const firePerimeterMap = useAppSelector(selectFirePerimetersMap)
    const allBufferLines = useAppSelector(selectAllFirePerimeterBufferLines)
    const visibleBufferLines = useMemo(
        () =>
            allBufferLines
                .filter((geometry) => geometry.id !== polygonBeingEditedId)
                .map((e, i) => ({ ...e, deckGlIdx: i, idx: i })),
        [allBufferLines, polygonBeingEditedId]
    )

    const generateColor = (geometry: Geometry) => {
        return geometry.color !== undefined
            ? convertHexStringToRGBArray(decodeNumberToColor(geometry.color))
            : geometry.id && firePerimeterMap[geometry.id]?.outline
              ? convertHexStringToRGBArray(
                    decodeNumberToColor(firePerimeterMap[geometry.id].outline!)
                )
              : defaultFirePerimeterColorDeckGL
    }

    return new GeometryLayer({
        id: "fire-perimeter-geometry-buffer",
        data: visibleBufferLines,
        visible: true,
        selectedId: selectedFirePerimeterId,
        extraLayers: [],
        styles: {
            points: {
                filled: true,
                stroked: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getFillColor: [255, 255, 255],
                lineWidthUnits: "pixels",
                getLineColor: generateColor,
            },
            selectedPoint: {
                filled: true,
                stroked: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getLineColor: [255, 255, 255],
                getLineWidth: 2,
                lineWidthUnits: "pixels",
                getFillColor: generateColor,
            },
            midpoints: {
                filled: true,
                stroked: true,
                radiusMinPixels: 4,
                radiusMaxPixels: 4,
                getFillColor: [255, 255, 255, 255],
                getLineWidth: 2,
                lineWidthUnits: "pixels",
                getLineColor: generateColor,
            },
            lines: {
                widthScale: 1,
                widthMaxPixels: 8,
                widthMinPixels: 2,
                capRounded: false,
                billboard: false,
                getColor: generateColor,
                getWidth: 2,
                miterLimit: 0,
            },
            polygons: {
                stroked: true,
                getLineWidth: (d: Geometry) => {
                    switch (d.width) {
                        case LineWidth.thick:
                            return 5
                        default:
                            return 3
                    }
                },
                lineWidthUnits: "pixels",
                lineMiterLimit: 0,
                getLineColor: generateColor,
                getFillColor: [0, 0, 0, 0],
                getFillPattern: (d: Line) => d.polygonPattern || "default",
                getFillPatternOffset: [0, 0],
            },
        },
    })
}
